import { faTimes } from '@fortawesome/pro-solid-svg-icons';
import { useState } from 'react';
import { FormattedMessage, defineMessages } from 'react-intl';
import styled from 'styled-components';

import { Sport } from '__generated__/globalTypes';
import ButtonBase from 'atoms/buttons/ButtonBase';
import { FontAwesomeIcon } from 'atoms/icons';
import { ChevronRightBold } from 'atoms/icons/ChevronRightBold';
import { SBHorizontal } from 'atoms/layout/flex';
import { LabelM, Text16 } from 'atoms/typography';
import { useCurrentUserContext } from 'contexts/currentUser';
import { useIntlContext } from 'contexts/intl';
import { useConversionCredit } from 'hooks/useConversionCredit';
import { ConversionCreditWithAmounts } from 'hooks/useConversionCredits';
import { glossary } from 'lib/glossary';
import { MonetaryAmountCurrency } from 'lib/monetaryAmount';
import { tabletAndAbove } from 'style/mediaQuery';

import { TermsDialog } from '../TermsDialog';
import coins from '../assets/coins.png';

export const messages = defineMessages({
  discountCredits: {
    id: 'ConversionCreditTinyBanner.discountCredits',
    defaultMessage: '{maxDiscount} credits',
  },
  discountCreditsUpTo: {
    id: 'ConversionCreditTinyBanner.discountCreditsUpTo',
    defaultMessage: '{percentageDiscount}% credits (up to { maxDiscount })',
  },
});

const Banner = styled.div`
  display: inline-flex;
  position: relative;
  padding: var(--half-unit) var(--unit);
  background: linear-gradient(99.55deg, #2575a2 0%, #008d85 100%);
  border-radius: var(--unit);
`;
const Content = styled(SBHorizontal).attrs({ gap: 0.5 })`
  position: relative;
  width: 100%;
`;
const StyledChevronRightBold = styled(ChevronRightBold)`
  color: var(--c-static-neutral-100);
  @media ${tabletAndAbove} {
    display: none;
  }
`;
const TermsLink = styled(ButtonBase)`
  text-decoration: underline;
  &:not(.forceShowTerms) {
    display: none;
  }
  @media ${tabletAndAbove} {
    display: inline;
  }
`;
const RemoveButton = styled(ButtonBase)`
  margin-left: var(--unit);
`;

type Props = {
  sport?: Sport;
  conversionCredit?: ConversionCreditWithAmounts;
  wrapTerms?: boolean;
  onRemove?: () => void;
};

export const ConversionCreditTinyBanner = ({
  sport,
  conversionCredit: conversionCreditProp,
  wrapTerms = true,
  onRemove,
}: Props) => {
  const { fiatCurrency } = useCurrentUserContext();
  const [openDialog, setOpenDialog] = useState<boolean>(false);
  const { formatNumber, formatDistanceToNowStrict } = useIntlContext();
  const currentUserConversionCredit = useConversionCredit(sport);
  const conversionCredit = conversionCreditProp || currentUserConversionCredit;
  if (!conversionCredit) return null;

  const { endDate, maxDiscount, percentageDiscount } = conversionCredit;
  const expiring = formatDistanceToNowStrict(endDate, {
    addSuffix: false,
  });

  const currencyCode = fiatCurrency.code.toLowerCase() as Exclude<
    MonetaryAmountCurrency,
    'wei'
  >;
  const maxDiscountInFiat =
    ((currencyCode in maxDiscount && maxDiscount?.[currencyCode]) || 0) / 100;

  return (
    <>
      <Banner>
        <Content>
          <img src={coins} alt="Coins" width={16} />
          <Text16 color="var(--c-static-yellow-300)">
            <FormattedMessage
              {...(percentageDiscount === 1
                ? messages.discountCredits
                : messages.discountCreditsUpTo)}
              values={{
                percentageDiscount: percentageDiscount * 100,
                maxDiscount: formatNumber(+maxDiscountInFiat, {
                  style: 'currency',
                  currency: fiatCurrency.code,
                }),
              }}
            />
          </Text16>
          <Text16 color="var(--c-static-neutral-100)">·</Text16>
          <Text16 color="var(--c-static-neutral-100)">{expiring}</Text16>
          {wrapTerms && (
            <>
              <LabelM color="var(--c-static-neutral-100)">
                <TermsLink type="button" onClick={() => setOpenDialog(true)}>
                  (
                  <FormattedMessage {...glossary.termsApply} />)
                </TermsLink>
              </LabelM>
              <StyledChevronRightBold onClick={() => setOpenDialog(true)} />
            </>
          )}
          {onRemove && (
            <RemoveButton type="button" onClick={onRemove} aria-label="Remove">
              <FontAwesomeIcon
                icon={faTimes}
                color="var(--c-static-neutral-100)"
              />
            </RemoveButton>
          )}
        </Content>
        {openDialog && <TermsDialog onClose={() => setOpenDialog(false)} />}
      </Banner>
      {!wrapTerms && (
        <LabelM color="var(--c-neutral-600)">
          <TermsLink
            className="forceShowTerms"
            type="button"
            onClick={() => setOpenDialog(true)}
          >
            <FormattedMessage {...glossary.termsApply} />
          </TermsLink>
        </LabelM>
      )}
    </>
  );
};
